import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../Config";
import "./styles/Login.css";
import "../styles/global.css";

// Roles permitted to sign in to the admin dashboard. Kept in sync with
// Users.js's ROLE_OPTIONS ("Admin", "Manager", "Staff", "User") — every
// role except "User" is allowed here. Compared in lowercase so it
// doesn't matter what casing the backend returns.
const ALLOWED_ADMIN_ROLES = ["admin", "staff", "manager"];

const Login = () => {
  // ─── ORIGINAL STATE — UNCHANGED ─────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [showPassword, setShowPassword] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const navigate = useNavigate();

  // ─── NEW UI-ONLY STATE (no impact on auth) ──────────────────────────────
  const [rememberMe, setRememberMe] = useState(false);

  // ─── ORIGINAL VALIDATION RULES — UNCHANGED ──────────────────────────────
  const validate = (data) => {
    const errors = {};
    if (!data.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!data.password) {
      errors.password = "Password is required.";
    } else if (data.password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }
    return errors;
  };

  const errors = validate(formData);
  const isValid = Object.keys(errors).length === 0;

  // ─── ORIGINAL HANDLERS — UNCHANGED logic ────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // ✨ ENHANCEMENT: clear API error message when user starts typing
    if (responseMessage) setResponseMessage("");
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  // ─── ORIGINAL handleSubmit — UNCHANGED API CALL & STORAGE ───────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true });
    if (!isValid) return;
    setLoading(true);
    setResponseMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}api/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
        mode: "cors",
      });
      const data = await res.json();

      // ─── AUTH SUCCESS CHECK — a 200 OK HTTP response only means the
      // request reached the server; it does NOT mean the credentials
      // were correct. This backend's own convention (see LoginRegister.js)
      // is to send `data.status: false` with an error `data.message` for
      // a failed login while still returning HTTP 200 — e.g. wrong
      // password. `res.ok` alone was letting that case slip through as a
      // successful login. We now require both res.ok AND data.status,
      // plus that a token/user actually came back.
      const loginSucceeded = res.ok && data?.status && data?.token && data?.user;

      if (loginSucceeded) {
        // ─── ROLE ACCESS GATE — only Admin / Staff / Manager may enter the
        // admin dashboard. Any other role (e.g. "User") or a missing/blank
        // role is rejected here, before anything is stored, so a valid
        // backend login for a non-admin account still can't get in.
        const userRole = (data?.user?.role || "").trim().toLowerCase();
        const isAllowedRole = ALLOWED_ADMIN_ROLES.includes(userRole);

        if (!isAllowedRole) {
          setResponseMessage("Only a valid admin user can log in here.");
          setLoading(false);
          return;
        }

        localStorage.setItem("authToken", data.token);
        localStorage.setItem("config_id", data.config_id);
        localStorage.setItem("user_id", data.user.id);
        localStorage.setItem("user", data.user);
        localStorage.setItem("name", data.user.name);
        localStorage.setItem("role", data.user.role);
        setResponseMessage("Login successful!");
        navigate("/dashboard");
      } else if (res.ok) {
        // HTTP request succeeded, but the backend flagged the login itself
        // as invalid (e.g. wrong password, wrong email). Explicitly clear
        // any old session data here — otherwise a leftover authToken from
        // a PREVIOUS successful login can still sit in localStorage, and
        // PrivateRoute (which only checks "does a token exist") would let
        // a wrong-password attempt look like it "logged in" simply because
        // an old session was never cleared.
        localStorage.removeItem("authToken");
        localStorage.removeItem("config_id");
        localStorage.removeItem("user_id");
        localStorage.removeItem("user");
        localStorage.removeItem("name");
        localStorage.removeItem("role");
        setResponseMessage(data?.message || "Incorrect email or password.");
      } else {
        const msgs = {
          400: "Bad request. Please check the input data.",
          401: "Incorrect email or password.",
          403: "You don't have permission to access this.",
          500: "Server error. Please try again later.",
        };
        setResponseMessage(msgs[res.status] || "Something went wrong.");
      }
    } catch {
      setResponseMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="login-root">
        {/* Decorative background blobs */}
        <div className="bg-blob bg-blob-1" />
        <div className="bg-blob bg-blob-2" />
        <div className="bg-blob bg-blob-3" />

        {/* Loading Overlay */}
        {loading && (
          <div className="loading-overlay">
            <div className="spinner-ring" />
          </div>
        )}

        {/* ── Left Panel (form) ── */}
        <div className="login-left">
          <div className="login-card">
            {/* Logo */}
            <a href="/" className="login-logo">
              <div className="login-logo-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v6M12 22v-6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M22 12h-6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24" />
                </svg>
              </div>
              <span className="login-logo-name">My Herbal Basket</span>
            </a>

            {/* Heading */}
            <h1 className="login-heading">Welcome back 👋</h1>
            <p className="login-subheading">Sign in to your admin dashboard to manage doctors, appointments, and more.</p>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate>
              {/* Email */}
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address</label>
                <div className="input-wrapper">
                  <input
                    id="email"
                    type="email"
                    name="email"
                    className={`form-input${touched.email && errors.email ? " has-error" : ""}${touched.email && !errors.email && formData.email ? " is-valid" : ""}`}
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="email"
                    autoFocus
                  />
                  <span className="input-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="m3 7 9 6 9-6" />
                    </svg>
                  </span>
                </div>
                {touched.email && errors.email && (
                  <div className="field-error">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {errors.email}
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="form-group">
                <label htmlFor="password" className="form-label">Password</label>
                <div className="input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className={`form-input password-input${touched.password && errors.password ? " has-error" : ""}${touched.password && !errors.password && formData.password ? " is-valid" : ""}`}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="current-password"
                  />
                  <span className="input-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="11" width="16" height="10" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      <circle cx="12" cy="16" r="1" />
                    </svg>
                  </span>
                  <button
                    type="button"
                    className="toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                        <line x1="2" y1="2" x2="22" y2="22" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
                {touched.password && errors.password && (
                  <div className="field-error">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {errors.password}
                  </div>
                )}
              </div>

              {/* Remember + Forgot */}
              <div className="form-row">
                <label className="remember-label">
                  <input
                    type="checkbox"
                    className="remember-checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  Remember me
                </label>
                <a href="#forgot-password" className="forgot-link">Forgot password?</a>
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="submit-btn"
                disabled={loading || !formData.email || !formData.password || (touched.email && touched.password && !isValid)}
              >
                {loading ? (
                  <>
                    <span className="btn-spinner" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Sign In
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>

              {/* Error alert */}
              {responseMessage && responseMessage !== "Login successful!" && (
                <div className="error-alert" role="alert">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {responseMessage}
                </div>
              )}
            </form>

            {/* Footer */}
            <div className="login-footer">
              © 2025 My Herbal Basket. All rights reserved.
            </div>
          </div>
        </div>

        {/* ── Right Panel (decorative) ── */}
        <div className="login-right">
          <div className="art-canvas">
            <div className="art-glow" />
            <div className="art-circle art-circle-1">
              <div className="art-dot art-dot-1" />
            </div>
            <div className="art-circle art-circle-2">
              <div className="art-dot art-dot-2" />
            </div>
            <div className="art-circle art-circle-3" />

            {/* Floating health icons */}
            <div className="float-icon float-icon-1">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
              </svg>
            </div>
            <div className="float-icon float-icon-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
            </div>
            <div className="float-icon float-icon-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 2v2M11 4a3 3 0 0 0-3 3v3a4 4 0 1 0 8 0V7a3 3 0 0 0-3-3M11 14v4a3 3 0 1 0 6 0v-1" />
                <circle cx="20" cy="10" r="2" />
              </svg>
            </div>
            <div className="float-icon float-icon-4">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L4 7v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V7l-8-5z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
          </div>

          <div className="art-tagline">
            <div className="art-tagline-text">
              Care that's<br />
              <span>always within reach.</span>
            </div>
            <div className="art-tagline-sub">
              Manage doctors, appointments, and patient records — all from one secure dashboard.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;